import type { BillingTransport } from '../billing-host/types';
import { SFE_FINGERPRINT_HEADER, sfeWebBuildFingerprint } from './sfe-web-build';

/**
 * How a billing write behaves when the server cannot be reached.
 * - `never`: the write is never queued. A `fetch()` network failure rejects
 *   with `OfflineNotQueuedError`, and no Idempotency-Key is minted.
 * - `offline-replay`: the write goes through the transport unchanged and is
 *   queued for the sync replay on a network failure.
 */
export type BillingQueuePolicy = 'never' | 'offline-replay';

export type BillingMutationMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface BillingMutationRule {
  readonly method: BillingMutationMethod;
  /** Matches the whole path; each `:param` segment is `[^/]+`. */
  readonly pattern: RegExp;
  readonly queue: BillingQueuePolicy;
  /** The act the write performs. */
  readonly act: string;
  /**
   * A fiscal act of the O8 list: it carries the X-SFE-Fingerprint header of
   * the bundle that sends it (src/lib/sfe-web-build.ts).
   */
  readonly fiscal: boolean;
}

function rule(
  method: BillingMutationMethod,
  template: string,
  queue: BillingQueuePolicy,
  act: string,
  fiscal = false
): BillingMutationRule {
  return { method, pattern: new RegExp(`^${template.replace(/:[A-Za-z]+/g, '[^/]+')}$`), queue, act, fiscal };
}

/**
 * Every non-GET path the billing api modules call, and the certification
 * writes of the settings.
 */
export const BILLING_MUTATIONS: readonly BillingMutationRule[] = [
  // Fiscal acts (#1203): replayed later, they would be performed at a moment
  // nobody chose.
  rule('POST', '/invoices/:id/sign', 'never', 'signature ou émission de la facture', true),
  rule('POST', '/invoices/:id/regularisation', 'never', 'régularisation d’un document provisoire', true),
  rule('POST', '/invoices/:id/cancel-refused-number', 'never', 'annulation du numéro refusé par le MCF', true),
  rule('POST', '/invoices/:id/payments', 'never', 'enregistrement d’un règlement', true),
  rule('POST', '/invoices/:id/payments/:paymentId/cancel', 'never', 'annulation d’un règlement', true),
  rule('POST', '/invoices/:id/credit-note', 'never', 'avoir sur une facture', true),
  rule('POST', '/invoices/credit-notes', 'never', 'avoir autonome', true),
  rule('POST', '/invoices/external-sales/:id/credit-note', 'never', 'avoir sur une vente externe', true),
  rule('POST', '/invoices/:id/debit-note', 'never', 'note de débit sur une facture', true),
  rule('POST', '/reports/a', 'never', 'émission du rapport A', true),
  rule('POST', '/reports/z', 'never', 'clôture de la période (rapport Z)', true),
  rule('POST', '/reports/fec', 'never', 'export du FEC'),
  rule('POST', '/mcf/signatures-en-doute/:id/abandon', 'never', 'signature en doute : non signée', true),
  rule('POST', '/mcf/signatures-en-doute/:id/signature', 'never', 'signature en doute : signée', true),
  rule('POST', '/mcf/signatures-en-doute/:id/serie', 'never', 'signature en doute : choix de la série', true),
  rule('POST', '/mcf/signatures-en-doute/:id/regularisation', 'never', 'signature en doute : régularisation', true),
  rule('POST', '/pos/transactions/:id/complete', 'never', 'finalisation de la vente', true),
  rule('POST', '/hospitalite/stays/:id/checkout', 'never', 'clôture du séjour', true),
  // Prints and reads sent as POST: a late replay has no printout or no reader.
  rule('POST', '/invoices/:id/duplicate', 'never', 'duplicata'),
  rule('POST', '/invoices/:id/original-release', 'never', 'remise de l’original'),
  rule('POST', '/invoices/:id/print-claims', 'never', 'réservation d’impression'),
  rule('POST', '/invoices/:id/print-claims/:claimId/renew', 'never', 'renouvellement de la réservation d’impression'),
  rule('POST', '/invoices/:id/print-claims/:claimId/cancel', 'never', 'annulation de la réservation d’impression'),
  rule('POST', '/invoices/:id/print-claims/:claimId/confirm', 'never', 'confirmation de la réservation d’impression'),
  rule('POST', '/invoices/match-lines', 'never', 'rapprochement des lignes lues avec le catalogue'),
  rule('POST', '/onboarding/spreadsheet/rows', 'never', 'lecture d’un classeur'),
  // A draft proposal is validated once: a queued replay could not be told
  // apart from a second validation.
  rule('POST', '/draft-proposals', 'never', 'enregistrement d’un fichier lu'),
  rule('POST', '/draft-proposals/:id/invoice', 'never', 'brouillon de facture depuis un fichier'),
  rule('POST', '/draft-proposals/:id/pos-cart', 'never', 'panier de caisse depuis un fichier'),
  // The till's session and edits of a sale: a queued edit would change a sale
  // the cart no longer shows, and a queued opening, movement or close would
  // replay the amount typed earlier at a moment nobody chose.
  rule('POST', '/pos/sessions', 'never', 'ouverture de caisse'),
  rule('POST', '/pos/sessions/:id/movements', 'never', 'mouvement de caisse'),
  rule('POST', '/pos/sessions/:id/close', 'never', 'fermeture de caisse'),
  rule('PUT', '/pos/transactions/:id/client', 'never', 'client de la vente'),
  rule('POST', '/pos/transactions/:id/items', 'never', 'ajout d’un article'),
  rule('PATCH', '/pos/transactions/:id/items/:lineId', 'never', 'quantité d’un article'),
  rule('DELETE', '/pos/transactions/:id/items/:lineId', 'never', 'retrait d’un article'),
  rule('POST', '/pos/transactions/:id/payments', 'never', 'ajout d’un paiement'),
  rule('DELETE', '/pos/transactions/:id/payments/:paymentId', 'never', 'retrait d’un paiement'),
  rule('POST', '/pos/transactions/:id/cancel', 'never', 'annulation de la vente'),
  rule('POST', '/pos/transactions/:id/suspend', 'never', 'suspension de la vente'),
  rule('POST', '/pos/transactions/:id/resume', 'never', 'reprise de la vente'),
  // The tenant's certification regime: replayed later, the change would
  // take effect at a moment nobody chose.
  rule('POST', '/settings/certification/category-periods', 'never', 'déclaration de la catégorie de contribuable'),
  rule('POST', '/settings/certification/attestations/:periodId', 'never', 'import de l’attestation de catégorie'),
  rule('POST', '/settings/certification/fiscal-config/import', 'never', 'import de la configuration fiscale signée'),
  rule('POST', '/settings/certification/no-mcf-transitional/opt-in', 'never', 'adhésion au régime transitoire sans MCF'),
  rule('POST', '/settings/certification/no-mcf-transitional/withdrawal', 'never', 'retrait du régime transitoire sans MCF'),
  // Drafts, sales and referential data: queued offline.
  rule('POST', '/invoices', 'offline-replay', 'création d’un brouillon'),
  rule('PUT', '/invoices/:id', 'offline-replay', 'modification d’un brouillon'),
  rule('DELETE', '/invoices/:id', 'offline-replay', 'suppression d’un brouillon'),
  rule('POST', '/invoices/external-sales', 'offline-replay', 'enregistrement d’une vente externe'),
  rule('PUT', '/invoices/external-sales/:id', 'offline-replay', 'modification d’une vente externe'),
  rule('DELETE', '/invoices/external-sales/:id', 'offline-replay', 'suppression d’une vente externe'),
  rule('POST', '/pos/transactions', 'offline-replay', 'création d’une vente'),
  rule('POST', '/settings/parameter-tables/:table/rows', 'offline-replay', 'ajout d’un code de paramétrage'),
  rule('PATCH', '/settings/parameter-tables/:table/rows/:code', 'offline-replay', 'complément d’un code de paramétrage'),
  rule('POST', '/invoices/match-lines/confirm', 'offline-replay', 'confirmation des rapprochements'),
  rule('POST', '/invoices/:id/comments', 'offline-replay', 'commentaire sur une facture'),
  rule('POST', '/invoices/:id/submit', 'offline-replay', 'soumission à la vérification'),
  rule('POST', '/invoices/:id/verify', 'offline-replay', 'vérification de la facture'),
  rule('POST', '/invoices/:id/withdraw', 'offline-replay', 'retrait de la vérification'),
  rule('DELETE', '/articles/:articleId/aliases/:aliasId', 'offline-replay', 'suppression d’un alias d’article'),
];

/** The row of a billing write, or undefined when the table has none. */
export function billingMutationRule(
  method: BillingMutationMethod,
  path: string
): BillingMutationRule | undefined {
  return BILLING_MUTATIONS.find((row) => row.method === method && row.pattern.test(path));
}

export interface BillingMutationOptions {
  /** Sent as the Idempotency-Key header. */
  idempotencyKey?: string;
}

/** The X-SFE-Fingerprint header of a fiscal row, when the bundle carries a fingerprint. */
function fiscalHeaders(row: BillingMutationRule): Readonly<Record<string, string>> | undefined {
  if (!row.fiscal) return undefined;
  const fingerprint = sfeWebBuildFingerprint();
  return fingerprint === null ? undefined : { [SFE_FINGERPRINT_HEADER]: fingerprint };
}

/**
 * Sends a billing write through `api` under its row's queue policy. A path
 * with no row is refused. A fiscal row carries the bundle's SFE fingerprint
 * header when the bundle has one. Otherwise an `offline-replay` row calls
 * the transport with the arguments given here, unchanged.
 */
export async function sendBillingMutation<T>(
  api: BillingTransport,
  method: BillingMutationMethod,
  path: string,
  ...payload: [body?: unknown, options?: BillingMutationOptions]
): Promise<T> {
  const row = billingMutationRule(method, path);
  if (!row) {
    throw new Error(`Écriture de facturation sans règle de file d'attente : ${method} ${path}`);
  }
  const headers = fiscalHeaders(row);
  if (row.queue === 'never') {
    const [body, options] = payload;
    const offline = { noQueue: true, failWhenOffline: true } as const;
    switch (method) {
      case 'POST':
        return api.post<T>(path, body, {
          ...offline,
          ...(options?.idempotencyKey !== undefined && { idempotencyKey: options.idempotencyKey }),
          ...(headers !== undefined && { headers }),
        });
      case 'PUT':
        return api.put<T>(path, body, offline);
      case 'PATCH':
        return api.patch<T>(path, body, offline);
      case 'DELETE':
        return api.delete<T>(path, offline);
    }
  }
  switch (method) {
    case 'POST':
      if (headers !== undefined) {
        const [body, options] = payload;
        return api.post<T>(path, body, { ...options, headers });
      }
      return api.post<T>(path, ...payload);
    case 'PUT':
      return payload.length === 0 ? api.put<T>(path) : api.put<T>(path, payload[0]);
    case 'PATCH':
      return payload.length === 0 ? api.patch<T>(path) : api.patch<T>(path, payload[0]);
    case 'DELETE':
      return api.delete<T>(path);
  }
}

/**
 * Mutation options of a never-queued write: React Query runs it while the
 * browser reports itself offline instead of pausing it until reconnection.
 */
export const NEVER_QUEUED = { networkMode: 'always' } as const;

/**
 * Mutation options of an `offline-replay` write: React Query runs it while
 * the browser reports itself offline, so the transport queues it, instead of
 * pausing it in memory until reconnection.
 */
export const OFFLINE_REPLAYED = { networkMode: 'always' } as const;
